import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, ShieldCheck, ArrowRight, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';
import { Reveal, SectionHead } from '../components/motion';
import { createCheckout } from '../lib/api';
import PayPalButton from '../components/PayPalButton';
import CryptoPayment from '../components/CryptoPayment';

const LOOKUP_KEYS = { online: 'selection_fee', office: 'selection_office' };

export default function SelectionPayment() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const plans = t('selection.plans');
  const initial = plans.findIndex((p) => p.key === params.get('plan'));
  const [selected, setSelected] = useState(initial >= 0 ? initial : 0);
  const [loading, setLoading] = useState(false);
  const plan = plans[selected];
  const lookupKey = LOOKUP_KEYS[plan.key];

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await createCheckout({ lookup_key: lookupKey, origin_url: window.location.origin, language: lang });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t('form.error'));
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-28 pb-24" data-testid="selection-payment-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline={t('selPay.chooseLabel')} title={t('selPay.title')} align="center" />
        <Reveal delay={0.1}>
          <p className="mt-5 text-center text-slate-400 max-w-2xl mx-auto" data-testid="selpay-sub">{t('selPay.sub')}</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-5 items-stretch" data-testid="selpay-plan-selector">
          {plans.map((p, i) => {
            const active = selected === i;
            const Icon = p.key === 'office' ? Building2 : Globe;
            return (
              <Reveal key={p.key} delay={i * 0.08} className="h-full">
                <button
                  type="button"
                  data-testid={`selpay-plan-${p.key}`}
                  onClick={() => setSelected(i)}
                  className={`relative w-full h-full rounded-xl border p-4 sm:p-6 text-center transition-all duration-300 ${
                    active
                      ? 'border-green-500 bg-[#1E293B] shadow-[0_0_45px_rgba(34,197,94,0.25)] sm:-translate-y-1.5 z-10'
                      : 'border-slate-700/70 bg-[#1E293B]/60 hover:border-slate-500'
                  }`}
                >
                  <span className={`absolute top-2 right-2 sm:top-3 sm:right-3 flex w-4 h-4 sm:w-5 sm:h-5 items-center justify-center rounded-full border-2 transition-colors ${active ? 'border-green-500 bg-green-500' : 'border-slate-600'}`}>
                    {active && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
                  </span>
                  <p className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-300">
                    <Icon className={`w-4 h-4 ${p.key === 'office' ? 'text-green-400' : 'text-blue-400'}`} />
                    {p.name}
                  </p>
                  <p className="mt-2 font-display text-2xl sm:text-4xl text-white whitespace-nowrap">{p.price}</p>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-6 rounded-xl border border-slate-700/70 bg-[#1E293B] p-5 sm:p-6" data-testid="selpay-plan-details">
            <p className="text-[11px] font-semibold tracking-widest text-slate-500 text-center">{plan.note}</p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl mx-auto">
              {plan.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-300">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-green-400" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-center text-xs text-slate-500">{plan.fit}</p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <button
              data-testid="selpay-submit-btn"
              onClick={pay}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-500 px-10 py-5 text-lg font-semibold text-white transition-all duration-300 hover:bg-green-400 hover:shadow-[0_0_40px_rgba(34,197,94,0.5)] hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
              {t('pay.payBtn')} · {plan.price}
            </button>
            <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="selpay-secure-note">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              {t('pay.note')}
            </p>
            <div className="mt-6">
              <PayPalButton lookupKey={lookupKey} />
            </div>
            <div className="mt-6 max-w-md mx-auto">
              <CryptoPayment lookupKey={lookupKey} amount={plan.price} />
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
